// PR Portal-V2-Partner-Introducer-Messaging (2026-06-26) — generic
// messaging hub for the Partner + Introducer portals.
//
// The Client portal scopes messaging to ONE app (the borrower has only
// the deals they're on). Partners and Introducers see many deals, so the
// hub adds a deal-picker above the MessagingPanel. Selecting a deal swaps
// the active app and the panel re-mounts (state reset on appId change in
// MessagingPanel.jsx).
//
// We deliberately REUSE components/client/MessagingPanel.jsx unchanged —
// it doesn't care whether the caller is a client, partner or introducer.
// Backend auth (user_can_view_application) gates posting per role.
//
// Props:
//   deals      — array of normalised deal records. Each must have:
//                  { id, application_reference, company_name, status,
//                    assigned_broker_name, assigned_broker_email, ... }
//                Partner + Introducer portals already shape their deal
//                lists this way (see /partner/all-deals + /introducer/dashboard).
//   title      — header label ("Messages" by default). Lets the partner
//                portal customise the heading without forking the file.
//   emptyHint  — small hint text under the header when deals.length === 0.
//
// Local state:
//   selectedDealId — persisted to sessionStorage per portal so a refresh
//   keeps the same deal active. We key by a salt prop so partner vs
//   introducer get separate stores.

import { useEffect, useMemo, useState } from "react";
import { MessageSquare } from "lucide-react";
import MessagingPanel from "@/components/client/MessagingPanel";

const _STORAGE_PREFIX = "solara.portal.messaging.selected.";

export default function PortalMessagingHub({
  deals = [],
  title = "Messages",
  emptyHint = "When you have an active deal you'll be able to message Solara about it here.",
  storageKey = "default",
}) {
  // Selected deal id — keep in state so re-renders don't blow it away.
  const [selectedDealId, setSelectedDealId] = useState(() => {
    try {
      return sessionStorage.getItem(_STORAGE_PREFIX + storageKey) || "";
    } catch { return ""; }
  });

  // If the persisted id isn't in the current deals list (e.g. a deal got
  // removed or a fresh login), fall back to the first deal.
  const resolvedDealId = useMemo(() => {
    if (deals.length === 0) return "";
    if (selectedDealId && deals.some((d) => d.id === selectedDealId)) return selectedDealId;
    return deals[0].id;
  }, [deals, selectedDealId]);

  useEffect(() => {
    if (!resolvedDealId) return;
    try { sessionStorage.setItem(_STORAGE_PREFIX + storageKey, resolvedDealId); } catch {}
  }, [resolvedDealId, storageKey]);

  const selectedDeal = useMemo(
    () => deals.find((d) => d.id === resolvedDealId) || null,
    [deals, resolvedDealId],
  );

  // Adapt the lean partner/introducer deal shape into what MessagingPanel
  // expects (an "application" with assigned_broker_* fields). The Hub
  // owns this adapter so MessagingPanel stays portal-agnostic.
  const applicationForPanel = useMemo(() => {
    if (!selectedDeal) return null;
    return {
      id: selectedDeal.id,
      application_reference: selectedDeal.application_reference || selectedDeal.reference || "",
      assigned_broker_name: selectedDeal.assigned_broker_name || "",
      assigned_broker_email: selectedDeal.assigned_broker_email || "",
      assigned_broker_phone: selectedDeal.assigned_broker_phone || "",
      company_borrower: { company_name: selectedDeal.company_name || selectedDeal.company || "" },
    };
  }, [selectedDeal]);

  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-xl p-4 sm:p-5" data-testid="portal-messaging-hub">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-serif text-base text-[#021d49] flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#bd9a59]" /> {title}
        </h2>
        {deals.length > 0 && (
          <span className="text-[11px] text-gray-500">
            {deals.length} deal{deals.length === 1 ? "" : "s"} available
          </span>
        )}
      </div>

      {deals.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">{emptyHint}</p>
      ) : (
        <>
          {/* Deal picker — select renders cleanly across mobile/desktop and
              avoids the weight of a tab strip for long deal lists. */}
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">
              About deal
            </label>
            <select
              value={resolvedDealId}
              onChange={(e) => setSelectedDealId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#bd9a59]/40"
              data-testid="portal-messaging-hub-deal-picker"
            >
              {deals.map((d) => {
                const label = `${d.company_name || d.company || "Unnamed"} · ${d.application_reference || d.reference || d.id.slice(0, 8)}`;
                return (
                  <option key={d.id} value={d.id}>{label}</option>
                );
              })}
            </select>
          </div>

          {applicationForPanel && (
            <MessagingPanel application={applicationForPanel} />
          )}
        </>
      )}
    </div>
  );
}
